import React, { useState, useMemo, useEffect } from 'react';
import { useQueryParam } from 'use-query-params';
import { Box } from '@material-ui/core';
import { PaneWrapper } from '@campfire/pane';
import { LinearProgressOverlay } from '@campfire/linear-progress-overlay';
import { SaveBulletinManagement } from './SaveBulletinManagement';
import { BulletinManagementList } from './BulletinManagementList';
import { BulletinPanel } from './BulletinPanel';
import { BulletinsToolbar } from './BulletinsToolbar';
import { GET_BULLETIN_MANAGEMENT_DATA } from './admin-console-bulletins-model.gql';
import {
  GetBulletinManagementData,
  GetBulletinManagementDataVariables,
} from './__generated__/GetBulletinManagementData';
import { BulletinManagement as IBulletin } from './__generated__/BulletinManagement';

import { useCampfireQuery } from '../../../global/network/useCampfireQuery';
import { useApiUrl } from '../../../global/config/useApiUrl';
import { useCampfireFetch } from '../../../global/network/useCampfireFetch';
import { useSnackbar } from '../../../global/config/useSnackbar';
import { useCanEditBulletinsState } from './use-can-edit-bulletins';
import { Page } from '../../../global/components';

export interface valuesToSubmit {
  bulletinId?: string;
  title: string;
  content: string;
  contentHtml?: string;
  pinned: boolean;
  isCommentable?: boolean;
  notifyVolunteers?: string | boolean;
  programIds: string[];
  activityIds: string[];
  roleIds: string[];
  permissionLevel?: string | null;
}

interface DeleteBulletin {
  bulletinId: string;
}

type Mode = 'list' | 'add' | 'edit';

interface BulletinManagementScreenProps {
  showAddAction?: boolean;
}

const BulletinManagementScreen = ({ showAddAction }: BulletinManagementScreenProps) => {
  const [mode, setMode] = useState<Mode>('list');
  const [search, setSearch] = useState('');
  const [isPinningId, setIsPinningId] = useState<string>('');
  const [bulletinQueryId, setBulletinQueryId] = useQueryParam('id');
  const [newBulletinParam, setNewBulletinParam] = useQueryParam('new');
  const apiUrl = useApiUrl();
  const { setSnackbar } = useSnackbar();

  const { data: bulletinQuery, loading: bulletinsLoading, refetch } = useCampfireQuery<
    GetBulletinManagementData,
    GetBulletinManagementDataVariables
  >(GET_BULLETIN_MANAGEMENT_DATA, {
    options: {
      variables: {
        programIds: [],
      },
    },
  });

  const { canEdit: canUserAddEdit, loading: canEditLoading } = useCanEditBulletinsState();

  const bulletins = useMemo(() => bulletinQuery?.vm.bulletins ?? [], [bulletinQuery]);

  // The open bulletin lives in the url, so it always reflects the freshest copy after a refetch.
  const activeBulletin = useMemo(() => bulletins.find((bulletin) => bulletin.bulletinId === bulletinQueryId), [
    bulletins,
    bulletinQueryId,
  ]);

  // The tab bar's New bulletin action arrives as a query param.
  useEffect(() => {
    if (newBulletinParam && !canEditLoading) {
      if (canUserAddEdit) {
        setMode('add');
      }
      setNewBulletinParam(undefined);
    }
  }, [newBulletinParam, canEditLoading, canUserAddEdit]);

  const { isLoading, run } = useCampfireFetch({ defer: true });

  const hitBulletinEndpoint = (endpoint: string, data: DeleteBulletin | valuesToSubmit): Promise<boolean> => {
    return run({
      url: `${apiUrl}/vm/bulletin/${endpoint}`,
      method: 'post',
      data: data,
    })
      .then(() => {
        if (refetch) refetch();
        return true;
      })
      .catch(() => {
        setSnackbar({
          open: true,
          message: `Error ${endpoint === 'delete' ? 'deleting' : 'adding/editing'} bulletin`,
          variant: 'error',
        });
        return false;
      });
  };

  const deleteBulletin = (bulletinId: string): Promise<void> =>
    hitBulletinEndpoint('delete', { bulletinId }).then((succeeded) => {
      if (!succeeded) return;
      setBulletinQueryId(undefined);
      setMode('list');
    });

  const submitBulletin = (bulletin: valuesToSubmit): Promise<boolean> => hitBulletinEndpoint('save', { ...bulletin });

  const saveBulletin = (bulletin: valuesToSubmit): Promise<void> =>
    submitBulletin(bulletin).then((succeeded) => {
      if (!succeeded) return;
      if (bulletin.bulletinId) {
        setBulletinQueryId(bulletin.bulletinId);
      }
      setMode('list');
    });

  const togglePinned = (bulletin: IBulletin): Promise<any> => {
    setIsPinningId(bulletin.bulletinId);
    return submitBulletin({
      bulletinId: bulletin.bulletinId,
      title: bulletin.title,
      content: bulletin.content,
      // Only read when notifying, which a pin toggle never does.
      contentHtml: '',
      pinned: !bulletin.pinned,
      isCommentable: bulletin.isCommentable,
      notifyVolunteers: false,
      programIds: bulletin.programs.map((program) => program.programId),
      roleIds: bulletin.activityRoles.map((role) => role.activityRoleId),
      activityIds: bulletin.activities.map((activity) => activity.activityId),
      permissionLevel: bulletin.permissionLevel,
    }).finally(() => setIsPinningId(''));
  };

  const openBulletin = (bulletin: IBulletin) => setBulletinQueryId(bulletin.bulletinId);

  const closeBulletin = () => setBulletinQueryId(undefined);

  // Closing the editor returns to the bulletin it was opened from, or to the plain list.
  const cancelAddEditBulletin = () => {
    if (mode === 'add') {
      setBulletinQueryId(undefined);
    }
    setMode('list');
  };

  if (mode !== 'list') {
    return (
      <Box position='relative' display='flex' flexDirection='column' flex={1} minHeight={0}>
        <LinearProgressOverlay isLoading={bulletinsLoading} />
        <SaveBulletinManagement
          cancel={cancelAddEditBulletin}
          submitBulletin={saveBulletin}
          deleteBulletin={deleteBulletin}
          activeBulletin={mode === 'edit' ? activeBulletin ?? null : null}
          isLoading={isLoading}
        />
      </Box>
    );
  }

  return (
    <Box position='relative' display='flex' flexDirection='column' flex={1} minHeight={0}>
      <LinearProgressOverlay isLoading={bulletinsLoading} />
      <BulletinsToolbar
        search={search}
        onSearchChange={setSearch}
        onAddBulletin={showAddAction && canUserAddEdit ? () => setMode('add') : undefined}
      />
      <Box display='flex' flex={1} minHeight={0}>
        <BulletinManagementList
          bulletins={bulletins}
          search={search}
          activeBulletinId={activeBulletin?.bulletinId}
          canUserAddEdit={canUserAddEdit}
          panelOpen={Boolean(activeBulletin)}
          isPinningId={isPinningId}
          onOpen={openBulletin}
          onTogglePinned={togglePinned}
        />

        {activeBulletin && (
          <BulletinPanel
            key={activeBulletin.bulletinId}
            bulletin={activeBulletin}
            canUserAddEdit={canUserAddEdit}
            isPinning={isPinningId === activeBulletin.bulletinId}
            onClose={closeBulletin}
            onEdit={() => setMode('edit')}
            onTogglePinned={() => togglePinned(activeBulletin)}
          />
        )}
      </Box>
    </Box>
  );
};

const StandaloneBulletinManagementScreen = () => (
  <Page>
    <PaneWrapper>
      <BulletinManagementScreen showAddAction />
    </PaneWrapper>
  </Page>
);

export { BulletinManagementScreen, StandaloneBulletinManagementScreen };
